import { type JSX, onCleanup, onMount, Show } from 'solid-js';
import { t } from '../i18n';
import type { LogLine } from '../lib/fleet-live';
import type { CoreModel } from '../lib/fleet-live-core';
import { createPipPainter } from '../lib/fleet-live-core-paint';
import type { LiveLoop } from '../lib/fleet-live-loop';
import {
  FLEET_TRANSFER_SLOT_ID,
  PIP_BIG_W,
  PIP_NARROW_BELOW_PX,
  pipCanvasHeight,
  pipSizeTag,
} from '../lib/fleet-live-pip';
import { createFloatingCore } from '../lib/fleet-live-pip-control';
import type { FleetCue } from '../lib/fleet-live-sound';
import { count, decimal } from '../lib/time';
import { claimTransferSlot } from '../lib/transfer-slot';
import { FleetLiveCanvas } from './FleetLiveCanvas';
import { FleetLiveIcon } from './FleetLiveIcon';

// The floating Core (#6779, prototype `:4202-4339`, CSS `:586-631`): once the Core panel's
// bottom edge scrolls above 40 px from the viewport top it pops in at the bottom right (16 px
// insets, safe-area aware), collapsed by default to a pill (the eye, "Core", "<n>/min",
// "Live"); expanded it carries the eye canvas, Agents / Commits/min / At the gate and the
// newest event, and resizes from its top-left handle by pointer or by key. Every control is a
// real button. When and how big are `createFloatingCore`'s (`lib/fleet-live-pip-control.ts`).
//
// #6773's transfer stack mounts into the slot directly above it ({@link FLEET_TRANSFER_SLOT_ID}).

export type FleetLivePipProps = {
  readonly model: CoreModel;
  readonly feed: LogLine | null;
  readonly loop: LiveLoop;
  readonly core: () => HTMLElement | undefined;
  readonly coreShown: () => boolean;
  readonly dialogOpen: () => boolean;
  readonly cue: (cue: FleetCue) => void;
  readonly onGoTo: () => void;
};

export function FleetLivePip(props: FleetLivePipProps): JSX.Element {
  const pip = createFloatingCore({
    loop: props.loop,
    core: () => props.core(),
    coreShown: () => props.coreShown(),
    dialogOpen: () => props.dialogOpen(),
    cue: (c) => props.cue(c),
  });
  const painter = createPipPainter();
  let canvas: HTMLCanvasElement | undefined;
  const min = (): boolean => pip.size().min;
  const narrow = (): boolean => pip.viewportW() < PIP_NARROW_BELOW_PX;
  const rate = (): string | null => {
    const r = props.model.commitsPerMinute;
    return r === null ? null : decimal(r);
  };
  const tag = () => pipSizeTag(pip.width());
  // The transfer stack's slot (#6773), claimed while this floating Core is rendered.
  let slotEl: HTMLDivElement | undefined;
  onMount(() => {
    if (slotEl !== undefined) onCleanup(claimTransferSlot(slotEl));
  });
  return (
    <div class="fleet-live__float">
      <div class="fleet-live__transfers" id={FLEET_TRANSFER_SLOT_ID} ref={slotEl} />
      <section
        ref={(el) => pip.attach(el)}
        class="fleet-live__pip"
        classList={{
          'fleet-live__pip--min': min(),
          'fleet-live__pip--big': !min() && pip.width() >= PIP_BIG_W,
          'fleet-live__pip--narrow': narrow(),
          'fleet-live__pip--resizing': pip.resizing(),
        }}
        style={min() ? {} : { width: `${pip.width()}px` }}
        hidden={!pip.visible()}
        aria-label={t('fleet.live.pip_label')}
      >
        <Show when={!min()}>
          <button
            type="button"
            class="fleet-live__pip-rs"
            aria-label={t('fleet.live.pip_resize')}
            title={t('fleet.live.pip_resize_title')}
            onKeyDown={(e) => pip.onResizeKey(e)}
            onPointerDown={(e) => pip.onResizeDown(e)}
            onPointerMove={(e) => pip.onResizeMove(e)}
            onPointerUp={(e) => pip.onResizeUp(e)}
            onPointerCancel={(e) => pip.onResizeUp(e)}
            onDblClick={() => pip.resetWidth()}
          >
            <FleetLiveIcon name="resize" />
          </button>
          <Show when={pip.resizing()}>
            <span class="fleet-live__pip-size" aria-hidden="true">
              {t('fleet.live.pip_size', { w: tag().w, h: tag().h })}
            </span>
          </Show>
        </Show>
        <div class="fleet-live__pip-head">
          <span class="fleet-live__pip-title">
            {min() ? t('fleet.live.pip_core') : t('fleet.live.core')}
          </span>
          <Show when={min() && rate()}>
            {(r) => (
              <span class="fleet-live__pip-mini">{t('fleet.live.pip_rate', { count: r() })}</span>
            )}
          </Show>
          <Show when={!(min() && narrow())}>
            <span class="fleet-live__pip-live">
              <i class="fleet-live__pip-dot" aria-hidden="true" />
              {t('fleet.live.pip_live')}
            </span>
          </Show>
          <button
            type="button"
            class="fleet-live__pip-b"
            aria-expanded={!min()}
            aria-label={min() ? t('fleet.live.pip_expand') : t('fleet.live.pip_collapse')}
            title={min() ? t('fleet.live.expand') : t('fleet.live.collapse')}
            onClick={() => pip.toggleMin()}
          >
            <FleetLiveIcon name={min() ? 'expand' : 'collapse'} />
          </button>
          <Show when={!min()}>
            <button
              type="button"
              class="fleet-live__pip-b"
              aria-label={t('fleet.live.pip_goto')}
              title={t('fleet.live.pip_goto')}
              onClick={() => props.onGoTo()}
            >
              <FleetLiveIcon name="up" />
            </button>
            <button
              type="button"
              class="fleet-live__pip-b"
              aria-label={t('fleet.live.pip_hide')}
              title={t('fleet.live.hide')}
              onClick={() => pip.dismiss()}
            >
              <FleetLiveIcon name="close" />
            </button>
          </Show>
        </div>
        <FleetLiveCanvas
          label={t('fleet.live.pip_canvas')}
          class="fleet-live__pip-cv"
          loop={props.loop}
          always
          onClick={() => props.onGoTo()}
          canvasRef={(el) => {
            canvas = el;
          }}
          width={() => (pip.visible() ? (min() ? 58 : pip.width() - 2) : 0)}
          height={(w) => (min() ? 30 : pipCanvasHeight(w))}
          paint={(ctx, p, w, f) => {
            const h = min() ? 30 : pipCanvasHeight(w);
            const at = pip.pointer();
            const r = canvas?.getBoundingClientRect();
            // `fitCanvas` hands back the canvas's own 2D context, typed down to `Paint2D`.
            painter.paint(ctx as Parameters<typeof painter.paint>[0], {
              w,
              h,
              mini: min(),
              p,
              model: props.model,
              moving: f.motion !== null,
              now: f.now,
              dt: f.dt,
              pointer:
                at === null || r === undefined
                  ? null
                  : { dx: at.x - (r.left + w / 2), dy: at.y - (r.top + h / 2), at: at.at },
            });
          }}
        />
        <Show when={!min()}>
          <div class="fleet-live__pip-stats">
            <span>
              <b>{count(props.model.total)}</b>
              {t('fleet.live.pip_agents')}
            </span>
            <Show when={rate()}>
              {(r) => (
                <span>
                  <b>{r()}</b>
                  {t('fleet.live.pip_commits')}
                </span>
              )}
            </Show>
            <span>
              <b>{count(props.model.atGateTotal)}</b>
              {t('fleet.live.pip_at_gate')}
            </span>
          </div>
          <Show when={props.feed}>
            {(line) => (
              <p class="fleet-live__pip-feed">
                <b>{line().chip}</b>
                {line().text}
              </p>
            )}
          </Show>
        </Show>
      </section>
    </div>
  );
}
